import type { DialogRoot } from '@base-ui/react/dialog';
import { Dialog } from '@base-ui/react/dialog';
import { useContext } from 'react';

import { cx } from '@hrworks/design-system/functions';
import { LocalizationContext } from '@hrworks/localization';

import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
import { ModalContent } from './Content/ModalContent';
import { ModalFooter } from './Footer/ModalFooter';
import { ModalHeader } from './Header/ModalHeader';
import { S } from './Modal.styles';
import type { ModalProps } from './Modal.types';

export const Modal = ({
  children,
  className,
  title,
  closeable,
  footer,
  size = 'small',
  height,
  width,
  fullScreen,
  onClose,
  closeOnBackdropClick = true,
  show,
  onExitComplete,
  ...otherProps
}: ModalProps) => {
  const { translate } = useContext(LocalizationContext);

  const onDialogOpenChange = (open: boolean, eventDetails: DialogRoot.ChangeEventDetails) => {
    if (open) return;
    if (!closeable) return;
    if (eventDetails.reason === 'outside-press' && !closeOnBackdropClick) return;
    onClose?.();
  };

  const onDialogOpenChangeComplete = (open: boolean) => {
    if (!open) onExitComplete?.();
  };

  return (
    <Dialog.Root open={show} onOpenChange={onDialogOpenChange} onOpenChangeComplete={onDialogOpenChangeComplete}>
      <Dialog.Portal>
        {!fullScreen && <Dialog.Backdrop forceRender render={<S.Backdrop className={S.cls.Backdrop} />} />}
        <S.Modal
          className={cx(S.cls.Modal, className)}
          $size={size}
          $height={height}
          $width={width}
          $fullScreen={fullScreen}
          {...otherProps}
        >
          <S.ScrollerContainer className={S.cls.ScrollerContainer}>
            {(title || closeable) && (
              <ModalHeader>
                {title && (
                  <S.TitleScroller className={S.cls.TitleScroller}>
                    <Dialog.Title render={<S.Title className={S.cls.Title} />}>{title}</Dialog.Title>
                  </S.TitleScroller>
                )}
                {closeable && (
                  <S.CloseButton
                    className={S.cls.CloseButton}
                    render={
                      <IconButton
                        size="small"
                        corner="rounded"
                        variant="subtle"
                        color="secondary"
                        aria-label={translate('modal-close')}
                      />
                    }
                  >
                    <Icon name="modal-close" />
                  </S.CloseButton>
                )}
              </ModalHeader>
            )}
            {children && <ModalContent>{children}</ModalContent>}
            {footer && <ModalFooter>{footer}</ModalFooter>}
          </S.ScrollerContainer>
        </S.Modal>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
